import React from 'react';
import { BadgeCheck, TrendingUp, FlaskConical } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ValueBand } from '../utils/programmeModel';
import ProvenanceBadge from './ProvenanceBadge';
import '../styles/ValueBands.css';

// ValueBands — the three value bands of the value-proven board (029-value-proven-
// dashboard, Increment C; User Story 2, FR-007/FR-008/FR-015/FR-016, SC-002/
// SC-003/SC-005/SC-008). A PURE view over the model's `bands` array: it renders
// EXACTLY the three ValueBand objects `deriveValueProven(...).bands` hands it, in
// the order the model guarantees ([proven, expected, speculative]). It does NOT
// fetch, derive, read the snapshot, or — load-bearing (SC-002) — SUM. Every
// figure is bound verbatim from props; the three read as three distinct things,
// never one total.
//
// Honesty contract:
//   • Proven (honest empty, SC-005): money === null renders the LITERAL string
//     "£— · none yet" — never £0, never a formatMoney figure; carries NO
//     illustrative badge and NO assumption affordance (no numeric figure shown).
//   • Expected + Speculative (any illustrative band, FR-007/SC-001): the money is
//     shown via the same FinOps formatMoney treatment the Dashboard uses
//     (£1.2m / £3.4m) AND the shared ProvenanceBadge.
//   • Money-as-assumption (FR-008/SC-003): every band that shows a numeric money
//     figure carries a visible "assumption →" disclosure surfacing that band's
//     `assumption` string as text.
//   • Reserved-colour (FR-016/SC-008): NEUTRAL chrome only (index.css tokens);
//     the bands NEVER spend the reserved coral (--ap-accent).
//   • a11y (FR-015): each label/figure/state is real text (meaning never by
//     colour alone); the lucide glyphs are decorative (aria-hidden).

// The FinOps money treatment, mirroring the (module-private) Dashboard
// `formatMoney` helper exactly: 1_610_000 -> "£1.6m", 55_000 -> "£55k". Mirrored
// rather than extracted to keep this change small and avoid restructuring the
// Dashboard's bundle for one figure; the contract is the abbreviated form only.
function formatMoney(value: number): string {
  const v = Math.abs(value);
  if (v >= 1_000_000) {
    const s = (value / 1_000_000).toFixed(1).replace(/\.0$/, '');
    return `£${s}m`;
  }
  if (v >= 1_000) return `£${Math.round(value / 1_000)}k`;
  return `£${Math.round(value)}`;
}

// The honest no-measurement string for the Proven band (SC-005). Not £0, never a
// formatMoney figure — the delivery loop has not run, so there is nothing banked.
const PROVEN_EMPTY_TEXT = '£— · none yet';

const BAND_ICON: Record<ValueBand['id'], LucideIcon> = {
  proven: BadgeCheck,
  expected: TrendingUp,
  speculative: FlaskConical,
};

const ValueBands: React.FC<{ bands: ValueBand[] }> = ({ bands }) => (
  <div className="ap-value-bands" role="group" aria-label="Value bands">
    {bands.map((band) => {
      const Icon = BAND_ICON[band.id];
      const hasMoney = band.money !== null;
      const isIllustrative = band.provenance === 'illustrative';
      return (
        <article key={band.id} className="ap-value-band">
          <div className="ap-value-band-head">
            <Icon className="ap-value-band-icon" size={16} aria-hidden={true} />
            <h3 className="ap-value-band-label">{band.label}</h3>
            {isIllustrative && <ProvenanceBadge source="illustrative" />}
          </div>

          {hasMoney ? (
            <>
              <p className="ap-value-band-figure">{formatMoney(band.money as number)}</p>
              {/* Money-as-assumption affordance (FR-008/SC-003): an affordance
                  whose label carries the word "assumption" and surfaces the band's
                  traceable assumption string as visible text. Not a <details> —
                  that carries an implicit role="group" that would collide with the
                  one bands group (FR-007/FR-015). */}
              <div className="ap-value-band-assumption">
                <span className="ap-value-band-assumption-label">assumption &rarr;</span>
                <p className="ap-value-band-assumption-text">{band.assumption}</p>
              </div>
            </>
          ) : (
            // Honest empty (SC-005): the literal no-measurement string, not £0.
            <p className="ap-value-band-figure ap-value-band-figure--none">{PROVEN_EMPTY_TEXT}</p>
          )}
        </article>
      );
    })}
  </div>
);

export default ValueBands;
